/**
 * Search query helpers — SKU-aware Algolia params.
 *
 * Ported from breitling-storefront-next (`getWatchFilters`, `getEyewearFilters`,
 * SearchView nested product indexes). Search and PLP both send `distinct=false`.
 * Full watch/eyewear SKUs use `typoTolerance=false`; partial prefixes and plain
 * text use `typoTolerance=min` (search context only).
 *
 * PLP keeps `distinct=false` everywhere — global distinct broke Gallet PLPs.
 */

import type { ProductType } from './filters';

/** @see breitling-storefront-next/src/integrations/algolia/helpers.ts */
export const WATCH_SKU_REGEX = /[A-Z]{1,2}\d{4,8}[A-Z]\d[A-Z]\d/i;

/**
 * Eyewear SKU pattern — e.g. JZ310615.25, JZ310615_25
 * @see breitling-storefront-next/src/integrations/algolia/helpers.ts
 */
export const EYEWEAR_SKU_REGEX = /^[A-Z]{2}\d{6}[._]\d{2}$/i;

export type SearchFilterContext = 'plp' | 'search' | 'components';

export interface SearchSkuAlgoliaParams {
  query?: string;
  distinct: boolean;
  typoTolerance: boolean | 'min';
  queryType: 'prefixAll';
  isFullSku: boolean;
}

export function isWatchSku(query: string): boolean {
  return WATCH_SKU_REGEX.test(query.trim());
}

export function isEyewearSku(query: string): boolean {
  return EYEWEAR_SKU_REGEX.test(query.trim());
}

export function isFullProductSku(query: string | undefined, productType: ProductType): boolean {
  if (!query?.trim()) return false;

  const trimmed = query.trim();

  if (productType === 'Watch') return isWatchSku(trimmed);
  if (productType === 'Eyewear') return isEyewearSku(trimmed);

  return false;
}

/**
 * Algolia search params for a free-text query, mirroring Breitling SearchView.
 *
 * Explicit PLP `filters.sku` (objectID refinement) bypasses this — callers keep
 * `distinct=false` and use `buildBaseFilterString({ sku })` instead.
 */
export function getSearchSkuAlgoliaParams(
  query: string | undefined,
  productType: ProductType,
  filterContext: SearchFilterContext
): SearchSkuAlgoliaParams {
  const trimmed = query?.trim();
  const queryType = 'prefixAll' as const;

  if (!trimmed) {
    return {
      query: undefined,
      distinct: false,
      typoTolerance: 'min',
      queryType,
      isFullSku: false,
    };
  }

  // PLP / CMS widgets: never enable distinct (BEC-000 PLP regression)
  if (filterContext === 'plp' || filterContext === 'components') {
    return {
      query: trimmed,
      distinct: false,
      typoTolerance: 'min',
      queryType,
      isFullSku: false,
    };
  }

  const isFullSku = isFullProductSku(trimmed, productType);

  return {
    query: trimmed,
    distinct: false,
    typoTolerance: isFullSku ? false : 'min',
    queryType,
    isFullSku,
  };
}
